import React, { useState } from 'react';
import { Field } from 'react-final-form';
import {
  Flex,
  FormField,
  FormHelp,
  IconButton,
  IconEyeClosedOutline,
  IconEyeOpenOutline,
  Input,
  Label,
} from '@zillow/constellation';
import { IMaskMixin } from 'react-imask';
import { FieldValidator } from '../../../validation/validation';
import { SSN } from '../../../constants/formFieldNames';
import { useTermSet } from '../../../contexts/IdVerificationContext';
import { TermSet } from '../../../../../models';

// The "size" and "as" props that come from the IMaskMixin have type definitions that are
// different than the "size" and "as" props that the Input component accept, so we don't
// want to spread those props into the Input component.
const MaskedSsnInput = IMaskMixin<HTMLInputElement>(({ inputRef, size, as, ...props }) => (
  <Input {...props} ref={inputRef} placeholder="XXX-XX-XXXX" autoComplete="off" />
));

export default function SocialSecurityNumberField() {
  const termSet = useTermSet();
  const [isSsnHidden, setIsSsnHidden] = useState(true);

  return (
    <Field
      name={SSN}
      render={({ input, meta }) => {
        const { touched, active, error } = meta;
        const isInvalid = touched && !active && Boolean(error);
        const { onChange, value: inputValue, ...restInput } = input;
        return (
          <Flex>
            <FormField
              optional={termSet === TermSet.RenterScreening}
              label={<Label>{termSet === TermSet.RenterScreening ? 'SSN or ITIN' : 'Social Security number'}</Label>}
              error={isInvalid}
              control={
                <Flex display="flex">
                  <MaskedSsnInput
                    mask="000-00-0000"
                    displayChar={isSsnHidden ? '*' : null}
                    onAccept={(value: unknown) => onChange(value)}
                    value={inputValue}
                    marginRight="xs"
                    {...restInput}
                  />
                  <IconButton
                    title={isSsnHidden ? 'Show social security number' : 'Hide social security number'}
                    icon={isSsnHidden ? <IconEyeClosedOutline size="sm" /> : <IconEyeOpenOutline size="sm" />}
                    onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                      e.preventDefault();
                      setIsSsnHidden(!isSsnHidden);
                    }}
                  />
                </Flex>
              }
              description={
                isInvalid ? (
                  <FormHelp>{error}</FormHelp>
                ) : (
                  <FormHelp>
                    {termSet === TermSet.RenterScreening
                      ? 'Add your SSN or ITIN to accurately verify your identity and pull the correct screening reports'
                      : 'Leave blank if you do not have one'}
                  </FormHelp>
                )
              }
            />
          </Flex>
        );
      }}
      validate={(ssn: string) => {
        // Remove all non-numeric characters
        const sanitizedSsn = ssn?.replace(/\D/g, '');
        const lengthErrorText =
          termSet === TermSet.RenterScreening
            ? 'Enter in a 9-digit SSN or ITIN'
            : 'Social Security number must be exactly 9 digits';
        const errors = new FieldValidator(sanitizedSsn).length(9, lengthErrorText).build();

        return errors ? errors : null;
      }}
    />
  );
}
